import React from 'react';
import styled from 'styled-components';
import { Card } from 'antd';
import { Doughnut } from 'react-chartjs-2';
import colors from 'Styles/Colors';

const CanceledOrdersCard = ({ data }) => {
  // Calculate canceled and total orders
  const totalOrders = data?.ordersList?.length || 0;
  const canceledOrdersCount = data?.ordersList?.filter(order => order.canceled)?.length || 0;
  const activeOrdersCount = totalOrders - canceledOrdersCount;

  // Chart data for canceled vs active orders
  const chartData = {
    labels: ['Canceled Orders', 'Active Orders'],
    datasets: [
      {
        data: [canceledOrdersCount, activeOrdersCount],
        backgroundColor: [
          '#EE5253', // Red for canceled
          '#10AC84' // Green for active
        ],
        borderWidth: 0,
        hoverOffset: 4
      }
    ]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        enabled: true,
        position: 'nearest',
        callbacks: {
          label: function(context) {
            const value = context.parsed;
            const percentage = totalOrders > 0 ? ((value / totalOrders) * 100).toFixed(1) : 0;
            return `${context.label}: ${value} (${percentage}%)`;
          }
        }
      }
    },
    elements: {
      arc: {
        borderWidth: 0
      }
    }
  };

  const canceledPercentage = totalOrders > 0 ? ((canceledOrdersCount / totalOrders) * 100).toFixed(1) : 0;

  return (
    <CanceledCard>
      <Card title="Canceled Orders" className="dashboard-card">
        <div className="chart-container">
          <div className="chart-wrapper">
            <Doughnut data={chartData} options={chartOptions} />
            <div className="center-value">
              <div className="canceled-count">{canceledOrdersCount}</div>
              <div className="label">Orders</div>
            </div>
          </div>
          <div className="stats">
            <div className="stat-item">
              <div className="stat-label">Total Orders</div>
              <div className="stat-value">{totalOrders}</div>
            </div>
            <div className="stat-item">
              <div className="stat-label">Cancellation Rate</div>
              <div className="stat-value canceled">{canceledPercentage}%</div>
            </div>
            <div className="stat-item">
              <div className="stat-label">Revenue from Canceled</div>
              <div className="stat-value">${(data?.cancellationRevenue || 0).toFixed(2)}</div>
            </div>
          </div>
        </div>

        <div className="breakdown">
          <div className="breakdown-item">
            <div className="color-indicator canceled"></div>
            <span className="label">Canceled Orders</span>
            <span className="count canceled">{canceledOrdersCount}</span>
          </div>
          <div className="breakdown-item">
            <div className="color-indicator active"></div>
            <span className="label">Active Orders</span>
            <span className="count">{activeOrdersCount}</span>
          </div>
        </div>

        <div className="description">Revenue from canceled orders that were not refunded</div>
      </Card>
    </CanceledCard>
  );
};

const CanceledCard = styled.div`
  flex: 1;
  min-width: 280px;

  .dashboard-card {
    height: 100%;
    overflow: visible;

    .ant-card-head-title {
      font-size: 16px;
      font-weight: 600;
      color: ${colors.text.primary};
    }

    .ant-card-body {
      padding: 16px;
      overflow: visible;
    }
  }

  .chart-container {
    display: flex;
    align-items: center;
    gap: 20px;
    margin-bottom: 20px;
    padding: 10px;
    overflow: visible;
    justify-content: center;
    flex-wrap: wrap;
  }

  .chart-wrapper {
    width: 140px;
    height: 140px;
    position: relative;
    flex-shrink: 0;
    overflow: visible;
    z-index: 10;

    .center-value {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      text-align: center;
      z-index: 5;
      pointer-events: none;

      .canceled-count {
        font-size: 28px;
        font-weight: 600;
        color: ${colors.error.primary};
        line-height: 1;
      }

      .label {
        font-size: 11px;
        color: ${colors.text.secondary};
        text-transform: uppercase;
        margin-top: 4px;
        line-height: 1.2;
      }
    }
  }

  .stats {
    flex: 1;

    .stat-item {
      margin-bottom: 12px;

      &:last-child {
        margin-bottom: 0;
      }

      .stat-label {
        font-size: 12px;
        color: ${colors.text.secondary};
        text-transform: uppercase;
        margin-bottom: 4px;
      }

      .stat-value {
        font-size: 18px;
        font-weight: 600;
        color: ${colors.text.primary};

        &.canceled {
          color: ${colors.error.primary};
        }
      }
    }
  }

  .breakdown {
    border-top: 1px solid ${colors.border.primary};
    padding-top: 16px;
    margin-bottom: 16px;
  }

  .breakdown-item {
    display: flex;
    align-items: center;
    margin-bottom: 8px;

    &:last-child {
      margin-bottom: 0;
    }

    .color-indicator {
      width: 12px;
      height: 12px;
      border-radius: 2px;
      margin-right: 12px;
      flex-shrink: 0;

      &.canceled {
        background-color: #ee5253;
      }
      &.active {
        background-color: #10ac84;
      }
    }

    .label {
      flex: 1;
      font-size: 14px;
      color: ${colors.text.primary};
    }

    .count {
      font-size: 14px;
      font-weight: 500;
      color: ${colors.text.primary};

      &.canceled {
        color: ${colors.error.primary};
      }
    }
  }

  .description {
    font-size: 12px;
    color: ${colors.text.secondary};
    text-align: center;
    font-style: italic;
  }

  @media (max-width: 768px) {
    min-width: 100%;

    .chart-container {
      flex-direction: column;
      text-align: center;
    }

    .chart-wrapper {
      width: 120px;
      height: 120px;
    }

    .stats {
      display: flex;
      justify-content: space-around;

      .stat-item {
        text-align: center;
        margin-bottom: 0;
      }
    }
  }
`;

export default CanceledOrdersCard;
